import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { SupabaseService } from '../../services/supabase.service';
import { RaceService } from '../../services/race.service';
import { Event as RaceEvent } from '../../models/data.models';
import { RESULT_DISCIPLINES } from '../../models/discipline.models';
import { FFSLC_REGIONS } from '../../data/ffslc-regions';
import { clipboardRows, looksLikeHeader, preparePastedTable, validatePastedTable, PASTE_FIELDS, PasteField, PastedTable } from '../../models/pasted-ranking';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';

interface RaceDraft {
  id: string; name: string; discipline: string; distance: number | null;
  text: string; original: string[][]; hasHeader: boolean; table: PastedTable | null; page: number;
}

@Component({
  selector: 'app-import-ranking', standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslatePipe, PageHeaderComponent],
  templateUrl: './import-ranking.component.html'
})
export class ImportRankingComponent {
  private db = inject(SupabaseService).client;
  private races = inject(RaceService);
  private translations = inject(TranslationService);
  step = signal<'event' | 'rankings' | 'success'>('event');
  mode: 'new' | 'existing' = 'new';
  event = { name: '', date: '', location: '', region: 'Hauts-de-France', club: '' };
  eventQuery = '';
  eventOptions = signal<RaceEvent[]>([]);
  selectedEvent = signal<RaceEvent | null>(null);
  searching = signal(false);
  searched = signal(false);
  searchError = signal(false);
  batches = signal<RaceDraft[]>([this.newBatch()]);
  activeId = signal(this.batches()[0].id);
  active = computed(() => this.batches().find(batch => batch.id === this.activeId())!);
  saving = signal(false);
  error = signal('');
  pastedError = signal('');
  receipt = signal<{ eventId: string; count: number } | null>(null);
  private requestId = crypto.randomUUID();
  private lastPayload = '';
  readonly fields = PASTE_FIELDS;
  readonly disciplines = RESULT_DISCIPLINES;
  readonly regions = [...new Set(FFSLC_REGIONS.flatMap(region => region.administrativeRegions))].sort();
  readonly rowPageSize = 50;
  checks = computed(() => this.batches().map(batch => ({ batch, validation: batch.table ? validatePastedTable(batch.table) : null })));
  total = computed(() => this.batches().reduce((sum, batch) => sum + (batch.table?.rows.length || 0), 0));
  ready = computed(() => this.total() > 0 && this.total() <= 5000 && this.checks().every(({ batch, validation }) =>
    !!batch.name.trim() && !!batch.discipline && !!batch.distance && batch.distance > 0 && batch.distance <= 999.99 &&
    !!validation?.results.length && validation.errors.length === 0) &&
    new Set(this.batches().map(batch => batch.name.trim().toLowerCase())).size === this.batches().length);

  private newBatch(): RaceDraft {
    return { id: crypto.randomUUID(), name: '', discipline: 'CanicrossLong', distance: null, text: '', original: [], hasHeader: true, table: null, page: 1 };
  }
  eventValid() { return this.mode === 'existing' ? !!this.selectedEvent() : !!(this.event.name.trim() && this.event.date && this.event.location.trim()); }
  next() { if (this.eventValid()) this.step.set('rankings'); }
  async searchEvents() {
    if (this.searching()) return;
    this.searching.set(true); this.searchError.set(false); this.searched.set(true);
    try { this.eventOptions.set((await this.races.getAdminEvents(1, this.eventQuery)).events); }
    catch { this.searchError.set(true); }
    finally { this.searching.set(false); }
  }
  patch(patch: Partial<RaceDraft>) {
    this.batches.update(batches => batches.map(batch => batch.id === this.activeId() ? { ...batch, ...patch } : batch));
  }
  addBatch() {
    if (this.batches().length >= 30) return;
    const batch = this.newBatch(); this.batches.update(batches => [...batches, batch]); this.activeId.set(batch.id);
  }
  removeBatch() {
    if (this.batches().length < 2) return;
    if (this.active().table?.rows.length && !confirm(this.translations.translate('pasteImport.removeConfirm'))) return;
    this.batches.update(batches => batches.filter(batch => batch.id !== this.activeId()));
    this.activeId.set(this.batches()[0].id);
  }
  onPaste(event: ClipboardEvent) {
    const clipboard = event.clipboardData;
    if (!clipboard) return;
    event.preventDefault();
    this.readPaste(clipboard.getData('text/plain'), clipboard.getData('text/html'));
  }
  readPaste(text = this.active().text, html = '') {
    this.pastedError.set('');
    if (this.active().table?.rows.length && !confirm(this.translations.translate('pasteImport.replaceConfirm'))) return;
    try {
      if (text.length > 2000000 || html.length > 5000000) throw new Error('size');
      const rows = clipboardRows(text, html);
      const hasHeader = looksLikeHeader(rows[0] || []);
      const table = preparePastedTable(rows, hasHeader);
      this.patch({ text, original: rows, hasHeader, table, page: 1 });
    } catch { this.pastedError.set('pasteImport.pasteError'); }
  }
  toggleHeader(hasHeader: boolean) {
    this.patch({ hasHeader, table: preparePastedTable(this.active().original, hasHeader), page: 1 });
  }
  mapColumn(index: number, field: string) {
    const table = this.active().table!;
    this.patch({ table: { ...table, mapping: table.mapping.map((value, column) => column === index ? field as PasteField : value) } });
  }
  editCell(row: number, column: number, value: string) {
    const table = this.active().table!;
    this.patch({ table: { ...table, rows: table.rows.map((cells, index) => index === row ? cells.map((cell, col) => col === column ? value : cell) : cells) } });
  }
  removeRow(row: number) {
    const table = this.active().table!;
    const rows = table.rows.filter((_, index) => index !== row);
    this.patch({ table: { ...table, rows }, page: Math.max(1, Math.min(this.active().page, Math.ceil(rows.length / this.rowPageSize))) });
  }
  validation() { return this.checks().find(item => item.batch.id === this.activeId())?.validation; }
  rowErrors(row: number) { return this.validation()?.errors.filter(error => error.row === row) || []; }
  async save() {
    if (!this.ready() || !this.eventValid() || this.saving()) return;
    this.error.set(''); this.saving.set(true);
    const payload = {
      eventId: this.mode === 'existing' ? this.selectedEvent()!.id : null,
      event: this.mode === 'new' ? this.event : null,
      races: this.checks().map(({ batch, validation }) => ({ name: batch.name.trim(), discipline: batch.discipline, distance: batch.distance, results: validation!.results }))
    };
    const fingerprint = JSON.stringify(payload);
    if (this.lastPayload && this.lastPayload !== fingerprint) this.requestId = crypto.randomUUID();
    this.lastPayload = fingerprint;
    try {
      const { data, error } = await this.db.rpc('import_pasted_ranking', { p_request_id: this.requestId, p_payload: payload });
      if (error) throw error;
      this.receipt.set(data); this.step.set('success');
    } catch (error: unknown) {
      this.error.set(error && typeof error === 'object' && 'message' in error ? String(error.message) : this.translations.translate('adminTools.error'));
    } finally { this.saving.set(false); }
  }
  reset() {
    this.event = { name: '', date: '', location: '', region: 'Hauts-de-France', club: '' };
    this.selectedEvent.set(null); this.mode = 'new'; this.error.set(''); this.pastedError.set('');
    const batch = this.newBatch(); this.batches.set([batch]); this.activeId.set(batch.id);
    this.requestId = crypto.randomUUID(); this.lastPayload = ''; this.receipt.set(null); this.step.set('event');
  }
}
